import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApiClient } from '../../api/apiClient';
import { RootState } from '../index';
import { handleDLOErrorWithFriendlyMessage } from '../utils/dloErrorHandler';
import { DLO_API_NAMES } from '../slices/dloSlice';

export const fetchDashboardSchedule = createAsyncThunk(
  'dlo/fetchDashboardSchedule',
  async (filters: {
    year?: string | number;
    period?: string | number;
    week?: string | number;
    createdBy?: string;
    modifiedBy?: string;
    headcount?: { min?: number; max?: number };
    actualKcph?: { min?: number; max?: number };
    forecastedHours?: { min?: number; max?: number };
    forecastedCases?: { min?: number; max?: number };
    actualCases?: { min?: number; max?: number };
  } = {}, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const locationId = state.appData?.selectedLocation?.id;

    if (!locationId) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DASHBOARD_SCHEDULE,
        'Missing location',
        true,
        { filters }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Build params object with filters
      const params: any = {
        locationId: locationId
      };

      // Add filters if they exist
      if (filters.year) {
        params.year = filters.year;
      }
      if (filters.period) {
        params.period = filters.period;
      }
      if (filters.week) {
        params.week = filters.week;
      }
      if (filters.createdBy) {
        params.createdBy = filters.createdBy;
      }
      if (filters.modifiedBy) {
        params.modifiedBy = filters.modifiedBy;
      }

      // Add range filters if they exist
      if (filters.headcount) {
        params.headcount = filters.headcount;
      }
      if (filters.actualKcph) {
        params.actualKcph = filters.actualKcph;
      }
      if (filters.forecastedHours) {
        params.forecastedHours = filters.forecastedHours;
      }
      if (filters.forecastedCases) {
        params.forecastedCases = filters.forecastedCases;
      }
      if (filters.actualCases) {
        params.actualCases = filters.actualCases;
      }

      console.log('DLO Dashboard Schedule API params:', params);

      const response = await api.get('/dlo/dashboard/schedule', {
        params: params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          
          Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
              if (typeof params[key] === 'object' && params[key] !== null) {
                // For nested objects like range filters, serialize as JSON
                searchParams.append(key, JSON.stringify(params[key]));
              } else {
                searchParams.append(key, params[key].toString());
              }
            }
          });
          
          return searchParams.toString();
        }
      });
      
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DASHBOARD_SCHEDULE,
        error,
        true,
        { filters, locationId }
      );
      return null;
    }
  }
);

export const fetchKCPHData = createAsyncThunk(
  'dlo/fetchKCPHData',
  async (chartFilters: { period?: number; week?: number; year?: number } = {}, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
        // Use chart filters if provided, otherwise fall back to Redux state
    const period = chartFilters?.period || state.appData?.periodWeek?.data?.period;
    const week = chartFilters.period
      ? (chartFilters.period - 1) * 4 + (chartFilters.week ?? 1)
      : state.appData?.periodWeek?.data?.week
        ? (state.appData?.periodWeek?.data?.period - 1) * 4 + state.appData?.periodWeek?.data?.week
        : 1;
    const year = chartFilters?.year || new Date().getFullYear();
    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_KCPH_DATA,
        'Missing location',
        true,
        { period, week, year }
      );
      return null;
    }

    if (!period || !week) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_KCPH_DATA,
        'Missing period or week',
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      const response = await api.get(
        `/dlo/kcph?loc_id=${loc_id}&year=${year}&period=${period}&week=${week}`
      );
      
      // Round off the KCPH values to 1 decimal place
      const rawData = response.data.data;
      const roundedData = {
        actualKcph: rawData.actualKcph !== null ? Math.round(rawData.actualKcph * 10) / 10 : null,
        targetKcph: rawData.targetKcph !== null ? Math.round(rawData.targetKcph * 10) / 10 : null,
        forecastedKcph: rawData.forecastedKcph !== null ? Math.round(rawData.forecastedKcph * 10) / 10 : null,
        scheduledKcph: rawData.scheduledKcph !== null ? Math.round(rawData.scheduledKcph * 10) / 10 : null,
        estimatedKcph: rawData.estimatedKcph !== null ? Math.round(rawData.estimatedKcph * 10) / 10 : null,
        adjustedRecommendedKcph: rawData.adjustedRecomendedKcph !== null ? Math.round(rawData.adjustedRecomendedKcph * 10) / 10 : null,
        crewedKcph: rawData.crewedKcph !== null ? Math.round(rawData.crewedKcph * 10) / 10 : null,
      };
      
      return roundedData;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_KCPH_DATA,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchDashboardHours = createAsyncThunk(
  'dlo/fetchDashboardHours',
  async (chartFilters: { period?: number; week?: number; year?: number } = {}, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    
    // Use chart filters if provided, otherwise fall back to Redux state
    const period = chartFilters?.period || state.appData?.periodWeek?.data?.period;
    const week = chartFilters.period
      ? (chartFilters.period - 1) * 4 + (chartFilters.week ?? 1)
      : state.appData?.periodWeek?.data?.week
        ? (state.appData?.periodWeek?.data?.period - 1) * 4 + state.appData?.periodWeek?.data?.week
        : 1;    
    const year = chartFilters?.year || new Date().getFullYear();

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DASHBOARD_HOURS,
        new Error('Missing location'),
        false,
        { period, week, year }
      );
      return null;
    }

    if (!period || !week) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DASHBOARD_HOURS,
        new Error('Missing period or week'),
        false,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      const response = await api.get(
        `/dlo/dashboard/hours?locationId=${loc_id}&year=${year}&period=${period}&week=${week}`
      );
      
      // Process and round the hours data
      const rawData = response.data.data;
      const processedData = rawData.map((dayData: any) => ({
        ...dayData,
        totalForecastHours: dayData.totalForecastHours !== null ? Math.round(dayData.totalForecastHours * 10) / 10 : 0,
        totalScheduledHours: dayData.totalScheduledHours !== null ? Math.round(dayData.totalScheduledHours * 10) / 10 : 0,
        totalActualHours: Math.round(dayData.totalActualHours * 10) / 10,
      }));
      console.log('Processed Dashboard Hours data:', processedData);
      return {
        data: processedData,
        summary: response.data.summary,
        totalRecords: response.data.totalRecords,
        filters: response.data.filters
      };
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DASHBOARD_HOURS,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchDSDZoneVolumeDistributionByDay = createAsyncThunk(
  'dlo/fetchDSDZoneVolumeDistributionByDay',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DSD_ZONE_VOLUME,
        new Error('Missing location'),
        false,
        undefined
      );
      return null;
    }

    try {
      const api = createApiClient();
      const response = await api.get(
        `/dlo/LocationSettings?loc_id=${loc_id}&type=DSD`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DSD_ZONE_VOLUME,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchNONDSDZoneVolumeDistributionByDay = createAsyncThunk(
  'dlo/fetchNONDSDZoneVolumeDistributionByDay',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    // For now commenting out period and week parameters as requested
    // const period = state.appData?.periodWeek?.data?.period;
    // const week = state.appData?.periodWeek?.data?.week;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_NON_DSD_ZONE_VOLUME,
        new Error('Missing location'),
        false,
        undefined
      );
      return null;
    }

    try {
      const api = createApiClient();
      // For now using the base URL without period and week
      const response = await api.get(
        `/dlo/LocationSettings?loc_id=${loc_id}&type=NON-DSD`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_NON_DSD_ZONE_VOLUME,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchDSDDailyPercentage = createAsyncThunk(
  'dlo/fetchDSDDailyPercentage',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DSD_DAILY_PERCENTAGE,
        new Error('Missing location'),
        false,
        undefined
      );
      return null;
    }

    try {
      const api = createApiClient();
      const response = await api.get(
        `/dlo/LocationSettings/Daily?loc_id=${loc_id}&type=DSD`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_DSD_DAILY_PERCENTAGE,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchNONDSDDailyPercentage = createAsyncThunk(
  'dlo/fetchNONDSDDailyPercentage',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_NON_DSD_DAILY_PERCENTAGE,
        new Error('Missing location'),
        false,
        undefined
      );
      return null;
    }

    try {
      const api = createApiClient();
      const response = await api.get(
        `/dlo/LocationSettings/Daily?loc_id=${loc_id}&type=NON-DSD`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_NON_DSD_DAILY_PERCENTAGE,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchShiftTimes = createAsyncThunk(
  'dlo/fetchShiftTimes',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SHIFT_TIMES,
        new Error('No location selected'),
        false
      );
      return null;
    }

    try {
      const api = createApiClient();
      const response = await api.get(
        `/dlo/ShiftTimes?loc_id=${loc_id}`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SHIFT_TIMES,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchHistoricalHours = createAsyncThunk(
  'dlo/fetchHistoricalHours',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_HISTORICAL_HOURS,
        new Error('Missing location'),
        false,
        undefined
      );
      return null;
    }

    try {
      const api = createApiClient();
      const response = await api.get(
        `/dlo/hours/location?loc_id=${loc_id}`
      );
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_HISTORICAL_HOURS,
        error,
        true,
        { locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchScheduleSettingsForecastDaily = createAsyncThunk(
  'dlo/fetchScheduleSettingsForecastDaily',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY,
        new Error('Missing location'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with required parameters for daily percentage data
      let url = `/dlo/ScheduleSettings/Forecast/Daily?loc_id=${loc_id}&type=DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching DSD forecast data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchScheduleSettingsForecastDailyNONDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsForecastDailyNONDSD',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY_NON_DSD,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with required parameters for daily percentage data
      let url = `/dlo/ScheduleSettings/Forecast/Daily?loc_id=${loc_id}&type=NON-DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) { 
        url += `&year=${year}`;
      }

      console.log('Fetching NON-DSD forecast data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_DAILY_NON_DSD,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

// New thunks for zone distribution data using the Forecast endpoint (not Daily)
export const fetchScheduleSettingsForecastZoneDistributionDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsForecastZoneDistributionDSD',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_DSD,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL for zone distribution data
      let url = `/dlo/ScheduleSettings/Forecast?loc_id=${loc_id}&type=DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching DSD zone distribution data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_DSD,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchScheduleSettingsForecastZoneDistributionNONDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsForecastZoneDistributionNONDSD',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_NON_DSD,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL for zone distribution data
      let url = `/dlo/ScheduleSettings/Forecast?loc_id=${loc_id}&type=NON-DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) { 
        url += `&year=${year}`;
      }

      console.log('Fetching NON-DSD zone distribution data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_FORECAST_ZONE_NON_DSD,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

// New thunks for actual zone distribution data using the Actual endpoint (not Daily)
export const fetchScheduleSettingsActualZoneDistributionDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsActualZoneDistributionDSD',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_DSD,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL for actual zone distribution data
      let url = `/dlo/ScheduleSettings/Actual?loc_id=${loc_id}&type=DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching DSD actual zone distribution data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_DSD,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchScheduleSettingsActualZoneDistributionNONDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsActualZoneDistributionNONDSD',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_NON_DSD,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL for actual zone distribution data
      let url = `/dlo/ScheduleSettings/Actual?loc_id=${loc_id}&type=NON-DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching NON-DSD actual zone distribution data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_ZONE_NON_DSD,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const fetchScheduleSettingsActualDaily = createAsyncThunk(
  'dlo/fetchScheduleSettingsActualDaily',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_SETTINGS_ACTUAL_DAILY,
        new Error('No location selected'),
        false,
        { period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with optional parameters
      let url = `/dlo/ScheduleSettings/Actual/Daily?loc_id=${loc_id}&type=DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching DSD actual data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      // TODO: Update to centralized error handling - API error
    }
  }
);

export const fetchScheduleSettingsActualDailyNONDSD = createAsyncThunk(
  'dlo/fetchScheduleSettingsActualDailyNONDSD',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      // TODO: Update to centralized error handling - Missing location;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with optional parameters
      let url = `/dlo/ScheduleSettings/Actual/Daily?loc_id=${loc_id}&type=NON-DSD`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching NON-DSD actual data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      // TODO: Update to centralized error handling - API error
    }
  }
);

// Fetch Schedule Settings Historical Hours
export const fetchScheduleSettingsHistoricalHours = createAsyncThunk(
  'dlo/fetchScheduleSettingsHistoricalHours',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      // TODO: Update to centralized error handling - Missing location;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with optional parameters
      let url = `/dlo/hours?loc_id=${loc_id}`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Fetching historical hours data from:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      // TODO: Update to centralized error handling - API error
    }
  }
);

export const updateScheduleSettingsForecastDaily = createAsyncThunk(
  'dlo/updateScheduleSettingsForecastDaily',
  async (params: { type: 'DSD' | 'NON-DSD'; updates: Array<{ dayNum: number; override_daily_pct: number }> }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      // TODO: Update to centralized error handling - Missing location;
    }

    // Validate that all 7 days are provided
    if (!params.updates || params.updates.length !== 7) {
      return rejectWithValue('All 7 days (1-7) are required for updates');
    }

    // Validate that days 1-7 are all present
    const dayNumbers = params.updates.map(update => update.dayNum).sort();
    const expectedDays = [1, 2, 3, 4, 5, 6, 7];
    const missingDays = expectedDays.filter(day => !dayNumbers.includes(day));
    
    if (missingDays.length > 0) {
      return rejectWithValue(`Missing data for days: ${missingDays.join(', ')}. All 7 days (1-7) are required for updates.`);
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with optional parameters
      let url = `/dlo/ScheduleSettings/Forecast/Daily?loc_id=${loc_id}&type=${params.type}`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Updating forecast data at:', url);
      const response = await api.put(url, { updates: params.updates });
      if (response.data?.success === false || response.data?.error || response.data?.message?.toLowerCase().includes('error')) {
        return rejectWithValue(response.data?.message || 'Unknown error');
      }
      return {
        success: true,
        message: response.data?.message || 'Forecast daily updated successfully',
        data: response.data
      };
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateScheduleSettingsActualDaily = createAsyncThunk(
  'dlo/updateScheduleSettingsActualDaily',
  async (params: { type: 'DSD' | 'NON-DSD'; updates: Array<{ dayNum: number; override_daily_cases: number; override_kcph: number }> }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      // TODO: Update to centralized error handling - Missing location;
    }

    // Validate that all 7 days are provided
    if (!params.updates || params.updates.length !== 7) {
      return rejectWithValue('All 7 days (1-7) are required for updates');
    }

    // Validate that days 1-7 are all present
    const dayNumbers = params.updates.map(update => update.dayNum).sort();
    const expectedDays = [1, 2, 3, 4, 5, 6, 7];
    const missingDays = expectedDays.filter(day => !dayNumbers.includes(day));
    
    if (missingDays.length > 0) {
      return rejectWithValue(`Missing data for days: ${missingDays.join(', ')}. All 7 days (1-7) are required for updates.`);
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with optional parameters
      let url = `/dlo/ScheduleSettings/Actual/Daily?loc_id=${loc_id}&type=${params.type}`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Updating actual data at:', url);
      const response = await api.put(url, { updates: params.updates });
      return response.data;
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      return rejectWithValue(errorMsg);
    }
  }
);

// Update Schedule Settings Forecast Zone Distribution
export const updateScheduleSettingsForecastZoneDistribution = createAsyncThunk(
  'dlo/updateScheduleSettingsForecastZoneDistribution',
  async (params: { type: 'DSD' | 'NON-DSD'; updates: Array<{ vpZone: string; dayNum: number; overridePct: number; overrideKcph: number }> }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_FORECAST_ZONE,
        new Error('Missing location'),
        false,
        { type: params.type, period, week, year }
      );
      return null;
    }

    // Validate that updates are provided
    if (!params.updates || params.updates.length === 0) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_FORECAST_ZONE,
        new Error('Zone distribution updates are required'),
        false,
        { type: params.type, period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with required parameters
      let url = `/dlo/ScheduleSettings/Forecast?loc_id=${loc_id}&type=${params.type}`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      console.log('Updating forecast zone distribution at:', url);
      
      // Prepare request body with all required parameters
      const requestBody = {
        loc_id: loc_id,
        period: period,
        week: week,
        type: params.type,
        updates: params.updates
      };
      
      console.log('Request body:', requestBody);
      const response = await api.put(url, requestBody);
      // If API returns a success: false or error message, treat as error
      if (response.data?.success === false || response.data?.error || response.data?.message?.toLowerCase().includes('error')) {
        handleDLOErrorWithFriendlyMessage(
          DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_FORECAST_ZONE,
          new Error(response.data?.message || 'Unknown error'),
          true,
          { type: params.type, period, week, year, locationId: loc_id }
        );
        return rejectWithValue(response.data?.message || 'Failed to update forecast zone distribution');
      }
      return {
        success: true,
        message: response.data?.message || 'Zone distribution updated successfully',
        data: response.data
      };
    } catch (error: any) {
      console.log('Error updating forecast zone distribution:', error);
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_FORECAST_ZONE,
        error,
        true,
        { type: params.type, period, week, year, locationId: loc_id }
      );
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update forecast zone distribution');
    }
  }
);

// Update Schedule Settings Actual Zone Distribution
export const updateScheduleSettingsActualZoneDistribution = createAsyncThunk(
  'dlo/updateScheduleSettingsActualZoneDistribution',
  async (params: { type: 'DSD' | 'NON-DSD'; updates: Array<{ vpZone: string; dayNum: number; overridePct: number; overrideKcph: number }> }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_ACTUAL_ZONE,
        new Error('Missing location'),
        false,
        { type: params.type, period, week, year }
      );
      return null;
    }

    // Validate that updates are provided
    if (!params.updates || params.updates.length === 0) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_ACTUAL_ZONE,
        new Error('Zone distribution updates are required'),
        false,
        { type: params.type, period, week, year }
      );
      return null;
    }

    try {
      const api = createApiClient();
      
      // Calculate week number based on period if available
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }
      
      // Build URL with required parameters
      let url = `/dlo/ScheduleSettings/Actual?loc_id=${loc_id}&type=${params.type}`;
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }
      
      console.log('Updating actual zone distribution at:', url);
      
      // Prepare request body with all required parameters
      const requestBody = {
        loc_id: loc_id,
        period: period,
        week: week,
        type: params.type,
        updates: params.updates
      };
      
      console.log('Request body:', requestBody);
      const response = await api.put(url, requestBody);
      if (response.data?.success === false || response.data?.error || response.data?.message?.toLowerCase().includes('error')) {
        handleDLOErrorWithFriendlyMessage(
          DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_ACTUAL_ZONE,
          new Error(response.data?.message || 'Unknown error'),
          true,
          { type: params.type, period, week, year, locationId: loc_id }
        );
        return rejectWithValue(response.data?.message || 'Failed to update actual zone distribution');
      }
      return {
        success: true,
        message: response.data?.message || 'Actual zone distribution updated successfully',
        data: response.data
      };
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_ACTUAL_ZONE,
        error,
        true,
        { type: params.type, period, week, year, locationId: loc_id }
      );
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update actual zone distribution');
    }
  }
);

// Update Schedule Settings Historical Hours
export const updateScheduleSettingsHistoricalHours = createAsyncThunk(
  'dlo/updateScheduleSettingsHistoricalHours',
  async (params: { 
    updates: any[]; 
    period?: string; 
    week?: string; 
  }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const selectedLocation = state.appData.selectedLocation;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    try {
      if (!selectedLocation?.id) {
        handleDLOErrorWithFriendlyMessage(
          DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_HISTORICAL_HOURS,
          new Error('No location selected'),
          false,
          { period, week, year }
        );
        return null;
      }

      // Calculate week number based on period and week from Redux state - same pattern as other functions
      let weekNumber = null;
      if (period && week) {
        weekNumber = (period - 1) * 4 + week;
      }

      // Build URL with optional query parameters
      let url = `/dlo/hours?loc_id=${selectedLocation.id}`;
      
      if (period) {
        url += `&period=${period}`;
      }
      if (week) {
        url += `&week=${week}`;
      }
      if (year) {
        url += `&year=${year}`;
      }

      const api = createApiClient();
      
      // Create request body
      const requestBody = {
        updates: params.updates
      };
      
      console.log('Historical Hours Update URL:', url);
      console.log('Historical Hours Request body:', requestBody);
      const response = await api.put(url, requestBody);
      if (response.data?.success === false || response.data?.error || response.data?.message?.toLowerCase().includes('error')) {
        handleDLOErrorWithFriendlyMessage(
          DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_HISTORICAL_HOURS,
          new Error(response.data?.message || 'Unknown error'),
          true,
          { period, week, year, locationId: selectedLocation.id }
        );
        return null;
      }
      return {
        success: true,
        message: response.data?.message || 'Historical hours updated successfully',
        data: response.data
      };
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SCHEDULE_SETTINGS_HISTORICAL_HOURS,
        error,
        true,
        { period, week, year, locationId: selectedLocation?.id }
      );
      return null;
    }
  }
);

// Update shift times
export const updateShiftTimes = createAsyncThunk(
  'dlo/updateShiftTimes',
  async (params: { shiftUpdates: any[] }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;

    if (!loc_id) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SHIFT_TIMES,
        new Error('No location selected'),
        false,
        params
      );
      return null;
    }

    try {
      const api = createApiClient();
      const requestBody = {
        loc_id: loc_id,
        shiftUpdates: params.shiftUpdates
      };
      
      console.log('Shift Times Update Request body:', requestBody);
      const response = await api.put('/dlo/ShiftTimes', requestBody);
      
      if (response.data?.success === false || response.data?.error) {
        handleDLOErrorWithFriendlyMessage(
          DLO_API_NAMES.UPDATE_SHIFT_TIMES,
          new Error(response.data?.message || 'Failed to update shift times'),
          true,
          { loc_id, shiftUpdates: params.shiftUpdates }
        );
        return null;
      }
      
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.UPDATE_SHIFT_TIMES,
        error,
        true,
        { loc_id, shiftUpdates: params.shiftUpdates }
      );
      return null;
    }
  }
);

export const fetchScheduleForecast = createAsyncThunk(
  'dlo/fetchScheduleForecast',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;
    if (!loc_id) {
      return rejectWithValue('Location ID is required');
    }
    
    if (!period) {
      return rejectWithValue('Period is required');
    }

    try {
      const api = createApiClient();
      
      console.log('Fetching schedule forecast with params:', { loc_id, period, week, year });
      
      const response = await api.get('/dlo/schedule-forecast', {
        params: {
          loc_id,
          period,
          week,
          year
        }
      });
      
      return response.data;
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_SCHEDULE_FORECAST,
        error,
        true,
        { period, week, year, locationId: loc_id }
      );
      return null;
    }
  }
);

export const cleanScheduleSettings = createAsyncThunk(
  'dlo/cleanScheduleSettings',
  async (params: { locId: string; period: number; week: number; year: number }, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      
      const payload = {
        locId: params.locId,
        period: params.period,
        week: params.week,
        year: params.year
      };

      console.log('Cleaning schedule settings with payload:', payload);

      const response = await api.post('/dlo/schedules/cleanSettings', payload);
      
      return response.data;
    } catch (error: any) {
      // Don't show error modal for lock errors - they are handled by the interceptors
      // The interceptor will show the lock conflict modal when 423 occurs
      // Just log the error and return null silently
      const errorMessage = String(error?.message || '').toLowerCase();
      
      console.log('[cleanScheduleSettings] Error caught:', {
        message: error?.message,
        isLockConflict: (error as any).isLockConflict,
        isLockAcquisitionFailed: (error as any).isLockAcquisitionFailed,
        status: error?.response?.status,
        hasLockKeyword: errorMessage.includes('lock') || errorMessage.includes('acquisition')
      });
      
      // Return null silently - don't show any error modal
      // Lock errors will be shown by SessionLockConflictModal from interceptors
      return null;
    }
  }
);

export const updateLocationSettingsHistoricalHours = createAsyncThunk(
  'dlo/updateLocationSettingsHistoricalHours',
  async (params: { 
    updates: any[]; 
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const selectedLocation = state.appData.selectedLocation;
      
      if (!selectedLocation?.id) {
        throw new Error('No location selected');
      }

      // Build URL without period/week parameters for location settings
      const url = `/dlo/hours/location?loc_id=${selectedLocation.id}`;

      const api = createApiClient();
      
      // Create request body
      const requestBody = {
        updates: params.updates
      };
      
      console.log('Location Settings Historical Hours Update URL:', url);
      console.log('Location Settings Historical Hours Request body:', requestBody);
      const response = await api.put(url, requestBody);
      return response.data;
    } catch (error: any) {
      // TODO: Update to centralized error handling - API error
    }
  }
);

export const fetchCreateScheduleHours = createAsyncThunk(
  'dlo/fetchCreateScheduleHours',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      return rejectWithValue('Missing location ID');
    }

    if (!period) {
      return rejectWithValue('Missing period');
    }

    try {
      const api = createApiClient();
      
      // Make single API call without roleType parameter
      const url = `/dlo/schedule/hours?loc_id=${loc_id}&year=${year}&period=${period}&week=${week}`;
      console.log('Create Schedule Hours API URL:', url);
      console.log('Parameters:', { loc_id, year, period, week });

      const response = await api.get(url);
      
      console.log('Schedule Hours API Response:', response.data);
      
      // Return the response data directly
      // The backend should now return all role types in a single response
      return {
        data: response.data,
        summary: {
          hasData: response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0
        }
      };
    } catch (error: any) {
      console.error('Create Schedule Hours API Error:', error);
      // TODO: Update to centralized error handling - API error
    }
  }
);

/**
 * Fetch Actual Hours data for the Actual Hours tab
 * Uses isActual=true parameter to get estimatedHours and actualHours
 */
export const fetchActualHours = createAsyncThunk(
  'dlo/fetchActualHours',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;

    if (!loc_id) {
      return rejectWithValue('Missing location ID');
    }

    if (!period) {
      return rejectWithValue('Missing period');
    }

    // Calculate parameters
    const currentYear = new Date().getFullYear();

    try {
      const api = createApiClient();
      
      // Make API call with isActual=true to get estimatedHours and actualHours
      const url = `/dlo/schedule/hours?loc_id=${loc_id}&year=${currentYear}&period=${period}&week=${week}&isActual=true`;
      console.log('Actual Hours API URL:', url);
      console.log('Parameters:', { loc_id, year: currentYear, period, week, isActual: true });

      const response = await api.get(url);
      
      console.log('Actual Hours API Response:', response.data);
      
      // Return the response data
      return {
        data: response.data,
        summary: {
          hasData: response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0
        }
      };
    } catch (error: any) {
      console.error('Actual Hours API Error:', error);
      // TODO: Update to centralized error handling - API error
    }
  }
);

export const fetchScheduleEmployeeData = createAsyncThunk(
  'dlo/fetchScheduleEmployeeData',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id) {
      return rejectWithValue('Missing location ID');
    }

    if (!period) {
      return rejectWithValue('Missing period');
    }

    if (!week) {
      return rejectWithValue('Missing week');
    }

    try {
      const api = createApiClient();

      const url = `/dlo/schedule?locId=${loc_id}&period=${period}&week=${week}&year=${year}`;
      console.log('Schedule Employee Data API URL:', url);
      console.log('Parameters:', { loc_id, period, week, year });

      const response = await api.get(url);
      
      console.log('Schedule Employee Data API Response:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error('Schedule Employee Data API Error:', error);
      // TODO: Update to centralized error handling - API error
    }
  }
);

/**
 * Fetch employee roles for DLO from the admin roles API.
 * Returns only Enabled roles available in "Labor Optimization", filtered by the
 * current location's locationType, organizationType, and is3pl values.
 * Normalised to { data: [{ loRole, roleType }] } so all existing consumers
 * (getRoleOptions, scheduleHelpers, etc.) keep working unchanged.
 */
/**
 * Fetch enabled Labour Optimization roles for the current location.
 *
 * Location context (locationTypes, organizationTypes, threePlStatuses) and
 * statuses: 'Enabled' are injected automatically by the API interceptor in
 * apiClient.ts — no need to pass them here. The interceptor reads these from
 * Redux state (selectedLocation) and appends them to every GET
 * /admin/role-management/roles request that does not set _skipLocationContext.
 *
 * Normalised to { data: [{ loRole, roleType }] } so all existing consumers
 * (getRoleOptions, scheduleHelpers, etc.) keep working unchanged.
 */
export const fetchEmployeeRoles = createAsyncThunk(
  'dlo/fetchEmployeeRoles',
  async (_, { getState, rejectWithValue }) => {
    try {
      const api = createApiClient();

      // statuses: 'Enabled' is also set here explicitly so callers that read
      // params directly (e.g. tests) see the intended value; the interceptor
      // treats a pre-existing statuses value as authoritative and won't override it.
      const params: Record<string, any> = {
        page: 0,
        size: 9999,
        statuses: 'Enabled',
        availableIns: 'Labor Optimization',
      };

      const response = await api.get('/admin/role-management/roles', { params });
      const raw: any[] = Array.isArray(response.data)
        ? response.data
        : (response.data?.content ?? []);

      // Normalise to the shape consumed by getRoleOptions: { data: [{ loRole, roleType, roleId }] }
      const data = raw.map((r: any) => ({
        loRole: r.name ?? r.roleName ?? r.roleName ?? '',
        roleType: r.roleType ?? r.locTypes?.[0] ?? 'Other',
        roleId: r.id ?? null,
      }));

      return { data };
    } catch (error: any) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.FETCH_EMPLOYEE_ROLES,
        error,
        true,
        {}
      );
      return rejectWithValue(error?.message ?? 'Failed to fetch employee roles');
    }
  }
);

/**
 * Fetch ALL Labour Optimization roles regardless of enabled/disabled status.
 * Used only when loading existing (past/submitted) schedules so that rows
 * referencing disabled roles can still resolve a role label in the dropdown.
 *
 * Location context (locationTypes, organizationTypes, threePlStatuses) is still
 * injected by the API interceptor. The automatic statuses: 'Enabled' injection
 * is suppressed via _skipStatusFilter: true so that disabled roles are included.
 */
export const fetchAllEmployeeRoles = createAsyncThunk(
  'dlo/fetchAllEmployeeRoles',
  async (_, { getState, rejectWithValue }) => {
    try {
      const api = createApiClient();

      const params: Record<string, any> = {
        page: 0,
        size: 9999,
        availableIns: 'Labor Optimization',
      };

      const response = await api.get('/admin/role-management/roles', {
        params,
        _skipStatusFilter: true,
      } as any);
      const raw: any[] = Array.isArray(response.data)
        ? response.data
        : (response.data?.content ?? []);

      const data = raw.map((r: any) => ({
        loRole: r.name ?? '',
        roleType: r.roleType ?? r.locTypes?.[0] ?? 'Other',
        roleId: r.id ?? null,
      }));

      return { data };
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to fetch all employee roles');
    }
  }
);

export const fetchEmployees = createAsyncThunk(
  'dlo/fetchEmployees',
  async (options: { bypassCache?: boolean } = {}, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const locationName = state.appData?.selectedLocation?.displayText;
    const { bypassCache = false } = options;

    if (!loc_id) {
      return rejectWithValue('Missing location ID');
    }

    if (!locationName) {
      return rejectWithValue('Missing location name');
    }

    // Check if employees are already cached in appData (only if not bypassing cache)
    if (!bypassCache) {
      const employeesData = state.appData?.employees;
      let cachedEmployees: any[] = [];
      
      if (employeesData) {
        // Handle both { data: [] } and [] formats
        cachedEmployees = Array.isArray(employeesData) ? employeesData : (employeesData.data || []);
      }
      
      if (cachedEmployees.length > 0) {
        // Filter out terminated employees (where Termination_Date is not null)
        const activeEmployees = cachedEmployees.filter(
          (emp: any) => emp.Termination_Date === null || emp.Termination_Date === undefined
        );
        
        console.log('Using cached employees from appData:', activeEmployees.length, 'active employees');
        return activeEmployees;
      }
    } else {
      console.log('Bypassing cache - making fresh API call for employees');
    }

    // If no cached employees, fetch from API
    try {
      const api = createApiClient();
      const response = await api.get(`/nho/employees?Location_ID=${loc_id}&Location_Name=${encodeURIComponent(locationName)}`);
      
      // Filter out terminated employees from API response
      // API returns an array directly, and Status field indicates if employee is "Active" or "Terminated"
      const apiData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const activeEmployees = apiData.filter((emp: any) => emp.Status === 'Active' || emp.Status !== 'Terminated');
      
      console.log('API returned employees, filtered to:', activeEmployees.length, 'active employees');
      return activeEmployees;
    } catch (error: any) {
      console.error('Fetch Employees API Error:', error);
      // TODO: Update to centralized error handling - API error
    }
  }
);

export const saveScheduleData = createAsyncThunk(
  'dlo/saveScheduleData',
  async (scheduleData: any[], { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState;
    const loc_id = state.appData?.selectedLocation?.id;
    const locationName = state.appData?.selectedLocation?.displayText;
    const period = state.dlo?.createSchedule?.period;
    const week = state.dlo?.createSchedule?.week;
    const year = state.dlo?.createSchedule?.year;

    if (!loc_id || !period || !week || !year) {
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.SAVE_SCHEDULE_DATA,
        new Error('Missing required parameters'),
        false,
        { period, week, year, locationId: loc_id }
      );
      return rejectWithValue('Missing required parameters');
    }
    
    try {
      const api = createApiClient();
      
      // Transform schedule data to include location metadata in each object
      const transformedScheduleData = scheduleData.map(item => ({
        ...item,
        locId: loc_id,
        locationName: locationName || '',
        period: period,
        week: week,
        year: year
      }));

      console.log('Save Schedule Data API Request (Array):', transformedScheduleData);
      
      const response = await api.post('/dlo/schedule', transformedScheduleData);
      
      console.log('Save Schedule Data API Response:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error('Save Schedule Data API Error:', error);
      const errorMessage = [error?.response?.data?.message, error?.response?.data?.error].filter(Boolean).join(' ') || error?.message || 'Failed to save schedule data';
      return rejectWithValue(errorMessage);
    }
  }
);

export const submitScheduleStatus = createAsyncThunk(
  'dlo/submitScheduleStatus',
  async (requestData: { locationName: string; period: number; week: number; year: number; status: string }, { dispatch }) => {
    try {
      const api = createApiClient();
      
      console.log('Submit Schedule Status API Request:', requestData);
      
      const response = await api.put('/dlo/schedule/status', requestData);
      
      console.log('Submit Schedule Status API Response:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error('Submit Schedule Status API Error:', error);
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.SUBMIT_SCHEDULE_STATUS,
        error,
        true,
        { 
          locationName: requestData.locationName,
          period: requestData.period,
          week: requestData.week,
          year: requestData.year,
          status: requestData.status
        }
      );
      return null;
    }
  }
);

export const copySchedule = createAsyncThunk(
  'dlo/copySchedule',
  async (payload: {
    targetPeriod: number;
    targetWeek: number;
    targetYear: number;
    targetLocationId: string;
    sourcePeriod: number;
    sourceWeek: number;
    sourceYear: number;
    sourceLocationId: string;
  }, { dispatch }) => {
    try {
      const api = createApiClient();
      const response = await api.post('/dlo/dashboard/copy-schedule', payload);
      return response.data;
    } catch (error: any) {
      console.error('Copy Schedule API Error:', error);
      handleDLOErrorWithFriendlyMessage(
        DLO_API_NAMES.COPY_SCHEDULE,
        error,
        true,
        { 
          targetPeriod: payload.targetPeriod,
          targetWeek: payload.targetWeek, 
          targetYear: payload.targetYear,
          sourcePeriod: payload.sourcePeriod,
          sourceWeek: payload.sourceWeek,
          sourceYear: payload.sourceYear
        }
      );
      return null;
    }
  }
);

// Re-export actual volume sync thunks
export { 
  distributeWeeklyOverrideToZones,
  recalculateWeeklyFromZones,
  setActualVolumeTabContext 
} from './actualVolumeSync';